import type {User} from 'nutshell-core/users/users-types';

import type {Tag} from 'shells/tags-editor/tags-editor';

import type {
    AccountLastContactedTimeFragment,
    AccountPageFragment,
    NutFileInput,
} from 'nutshell-graphql-types';

import {PeepHeaderActionBar} from '../../shared/header/action-bar/peep-header-action-bar';
import {PeepDescriptionContainer} from '../../shared/header/description/peep-description-container';
import {PeepMap} from '../../shared/header/map/peep-map';
import {PeepHeaderRightLayout} from '../../shared/header/peep-header-right-layout';
import {PeepTagsEditor} from '../../shared/tags-editor/peep-tags-editor';

import {AccountAvatar} from './account-avatar';
import {AccountContextOverview} from './account-context-overview';
import {AccountDuplicateWrapper} from './account-duplicate-wrapper';
import {AccountEditFields} from './account-edit-fields';

import peepPageStyles from '../../peep-page.module.css';

interface Props {
    account: AccountPageFragment;
    accountLastContact: AccountLastContactedTimeFragment | null;
    isLoadingAccountLastContact: boolean;
    user: User;
    onUpdateAccountName: (name: string) => Promise<any>;
    onUpdateAvatar: (avatarFile: NutFileInput | null) => Promise<any>;
    onUpdateAssignee: (assigneeId: string | null) => Promise<any>;
    onUpdateDeletedStatus: (isDeleted: boolean) => Promise<any>;
    onUpdateDescription: (description: string) => Promise<any>;
    onHardDelete: () => Promise<any>;
    onUpdateAccountTerritory: (
        territoryId: string | null,
        territoryName: string | null
    ) => Promise<any>;
    onUpdateAccountType: (accountTypeId: string, accountTypeName: string) => Promise<any>;
    onUpdateAccountIndustry: (
        industryId: string | null,
        industryName: string | null
    ) => Promise<any>;
    onUpdateAccountRevenue: (revenue: number) => Promise<any>;
    onUpdateAccountEmployeeCount: (employeeCount: number) => Promise<any>;
    onUpdateTags: (tags: Tag[]) => Promise<any>;
}

export function AccountHeader(props: Props) {
    const childrenCount = props.account.childAccounts.totalCount ?? 0;
    const hasChildren = childrenCount > 0;
    const hasParentAccount = props.account.parentAccount !== null;
    const hasHierarchy = hasParentAccount || hasChildren;
    const hasAtLeastOneEnabledEditField = props.account.headerFields.some((f) => f.isEnabled);

    return (
        <div className={peepPageStyles['header-container']}>
            <PeepMap
                peepType='account'
                locationCoordinates={
                    props.account.postalAddresses.primary?.location?.coordinates ?? null
                }
                locationWeather={props.account.postalAddresses.primary?.location?.weather ?? null}
            />
            <div className={peepPageStyles['header-content']}>
                <div className={peepPageStyles['avatar-column']}>
                    <AccountAvatar account={props.account} onUpdateAvatar={props.onUpdateAvatar} />
                    {!NutClientConfig.hasCoreEntityFullHeightSidebarFF && (
                        <AccountDuplicateWrapper accountId={props.account.id} />
                    )}
                </div>
                <PeepHeaderRightLayout>
                    <PeepHeaderActionBar
                        peep={props.account}
                        user={props.user}
                        assigneeId={props.account.owner?.id ?? null}
                        entityType='account'
                        assigneePlaceholder='Assign this company…'
                        onUpdateName={props.onUpdateAccountName}
                        onUpdateAssignee={props.onUpdateAssignee}
                        onUpdateDeletedStatus={props.onUpdateDeletedStatus}
                        onHardDelete={props.onHardDelete}
                        nameAdornment={
                            NutClientConfig.hasCoreEntityFullHeightSidebarFF ? (
                                <AccountDuplicateWrapper accountId={props.account.id} />
                            ) : undefined
                        }
                    />
                    {hasHierarchy && NutClientConfig.hasAccountHierarchy && (
                        <div className={peepPageStyles['context-overview-container']}>
                            <AccountContextOverview account={props.account} />
                        </div>
                    )}
                    <div className='pl-2'>
                        <PeepDescriptionContainer
                            entityType='account'
                            value={props.account.description}
                            onSaveNewValue={props.onUpdateDescription}
                        />
                    </div>
                    {!NutClientConfig.hasCoreEntityFullHeightSidebarFF && (
                        <>
                            {hasAtLeastOneEnabledEditField && (
                                <div className={peepPageStyles['edit-fields-container']}>
                                    <AccountEditFields
                                        account={props.account}
                                        accountLastContact={props.accountLastContact}
                                        isLoadingAccountLastContact={
                                            props.isLoadingAccountLastContact
                                        }
                                        canAccessSetup={props.user.permissions.canAccessSetup}
                                        onUpdateAccountTerritory={props.onUpdateAccountTerritory}
                                        onUpdateAccountType={props.onUpdateAccountType}
                                        onUpdateAccountIndustry={props.onUpdateAccountIndustry}
                                        onUpdateAccountRevenue={props.onUpdateAccountRevenue}
                                        onUpdateAccountEmployeeCount={
                                            props.onUpdateAccountEmployeeCount
                                        }
                                    />
                                </div>
                            )}
                            <div className={peepPageStyles['tags-editor-container']}>
                                <PeepTagsEditor
                                    entityId={props.account.id}
                                    entityType='ACCOUNT'
                                    user={props.user}
                                    tagMaps={props.account.tagMaps?.edges}
                                    onUpdateTags={props.onUpdateTags}
                                />
                            </div>
                        </>
                    )}
                </PeepHeaderRightLayout>
            </div>
        </div>
    );
}
